package com.evoapps.evoshift.ui.notifications

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.evoapps.evoshift.domain.model.Notification
import com.evoapps.evoshift.domain.model.NotificationCategory
import com.evoapps.evoshift.domain.model.NotificationSettings
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.repository.NotificationSettingsRepository
import com.evoapps.evoshift.shell.AppShell
import com.evoapps.evoshift.shell.SystemNotification
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.notif_absence_public_text
import evoshift.composeapp.generated.resources.notif_absence_public_title
import kotlinx.coroutines.flow.flowOf
import org.jetbrains.compose.resources.stringResource
import org.kodein.di.compose.rememberInstance
import com.evoapps.evoshift.domain.util.AppClock
import kotlin.time.Duration.Companion.minutes
import kotlin.time.Instant

/**
 * Which in-app notifications are new while the app runs (SH-141, the Mac app's system
 * notifications). The first list it sees is what was there already; later lists bring the new
 * ones, unread, created since [since] (not the backlog that arrives when the cache catches up with
 * the server after a while offline) and pushed by the person's settings ([NotificationSettings],
 * the same switches as the phone's push, SH-73). Pure, so it is tested without a window.
 */
class NewNotifications(private val since: Instant) {
    private var seeded = false
    private val seen = mutableSetOf<String>()

    /** The new notifications of [items] (newest first, as observed), oldest first. */
    fun take(items: List<Notification>, settings: NotificationSettings): List<Notification> {
        val fresh = items.filter { it.key() !in seen }
        fresh.mapTo(seen) { it.key() }
        if (!seeded) {
            seeded = true
            return emptyList()
        }
        return fresh
            .filter { !it.isRead && it.isSince(since) && settings.pushes(it) }
            .sortedBy { it.createdAt }
    }

    private fun Notification.key(): String = id.ifEmpty { "$createdAt:$title" }

    private fun Notification.isSince(since: Instant): Boolean =
        runCatching { Instant.parse(createdAt) >= since }.getOrDefault(true)
}

/**
 * Hands the in-app notifications that arrive while the app runs to the platform shell (SH-141,
 * [AppShell.notify]): the Mac app shows them in macOS's notification centre. The texts are the
 * notifications list's own, in the reader's language; an absence request names nobody, as on a
 * locked phone (SH-50). [loaded]: [items] are the person's real list, not the empty one before it
 * loads (which would make everything look new).
 */
@Composable
fun ShellNotifications(shell: AppShell, user: User?, items: List<Notification>, loaded: Boolean) {
    val settingsRepository: NotificationSettingsRepository by rememberInstance()
    val uid = user?.uid
    val settings by remember(uid, settingsRepository) {
        uid?.let { settingsRepository.observe(it) } ?: flowOf(null)
    }.collectAsState(null)
    // A new person (signing out and in as someone else) starts afresh.
    val tracker = remember(uid) { NewNotifications(since = AppClock.now() - 1.minutes) }
    var toPost by remember(uid) { mutableStateOf(emptyList<Notification>()) }
    LaunchedEffect(tracker, items, settings, loaded) {
        val current = settings ?: return@LaunchedEffect
        if (!loaded || uid == null) return@LaunchedEffect
        val fresh = tracker.take(items, current)
        if (fresh.isNotEmpty()) toPost = toPost + fresh
    }
    toPost.forEach { notification ->
        key(notification.id, notification.createdAt) {
            val private = NotificationCategory.of(notification) == NotificationCategory.ABSENCES
            val title = if (private) stringResource(Res.string.notif_absence_public_title) else notificationTitle(notification)
            val body = if (private) stringResource(Res.string.notif_absence_public_text) else notificationBody(notification).orEmpty()
            LaunchedEffect(Unit) {
                shell.notify(SystemNotification(notification.id, title, body))
                toPost = toPost - notification
            }
        }
    }
}
